import * as React from 'react'
import Page from 'components/presenter/page/Page'
import { Paper, IconButton } from '@material-ui/core'
import { DropzoneAreaBase } from 'material-ui-dropzone'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import Image from 'material-ui-image'
import { useSnackbar } from 'libs/ui/snackbar'
import CropperDialog from 'components/presenter/form/CropperDialog'
import CloseIcon from '@material-ui/icons/Close'
import HowItDesignSection from 'components/container/page/landing/HowItDesignSection' 
import FillButton from 'components/presenter/buttons/FillButton'
import cn from 'classnames'
import * as styles from './create.module.css'

const UploadLeafletForm = ({
  selectedFiles = [],
  onChanged,
}) => {
  const { t } = useTranslation()
  const { showError } = useSnackbar()
  const [isCropOpen, setCropOpen] = React.useState(false)
  const [selectedFile, setSelectedFile] = React.useState(null)


  const [items, setItems] = React.useState(selectedFiles)
  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)
    return result
  }

  const handleClickOpenCropDialog = () => {
    setCropOpen(true)
  }

  const handleCloseCropDialog = () => {
    setCropOpen(false)
  }

  const onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return
    }

    const reOrderedList = reorder(
      items,
      result.source.index,
      result.destination.index
    )
    setItems(reOrderedList)
    if (onChanged) {
      onChanged(reOrderedList)
    }
  }

  const onAddImage = (files) => {
    if (items.length >= 5) {
      showError(t('jetso.maxLeafletError'))
      return
    }    
    const file = files[0]
    setSelectedFile(file)
    handleClickOpenCropDialog()
  }

  const _addImage = (blob, file = {}) => {
    const newItemList = [
      ...items,
      {
        file,
        crop: {
          blob,
          blobUrl: URL.createObjectURL(blob),
        },
        now: Date.now()
      }
    ]
    setItems(newItemList)
    if (onChanged) {
      onChanged(newItemList)
    }
  }

  const _onCrop = (blob) => {
    handleCloseCropDialog()
    _addImage(blob, selectedFile)
  }

  const onCancel = (index) => {
    items.splice(index, 1)
    const newItemList = [
      ...items
    ]
    setItems(newItemList)
    if (onChanged) {
      onChanged(newItemList)
    }
  }

  const isEmptyImages = () => items.length === 0

  return (
    <Page className={styles.root} seoMeta={{
      title: t('jetso.uploadLeaflet')
    }}>
      <div className={styles.titleSection}>
        <h1 className={styles.title}>{t('jetso.uploadLeaflet')}</h1>
        <p className={styles.subTitle}>{t('jetso.uploadLeafletDescription')}</p>
      </div>

      {
        !isEmptyImages() &&
        <>
          <Paper className={styles.previewRoot} elevation={2}>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="droppable" direction="horizontal">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    className={cn(styles.preview, snapshot.isDraggingOver ? styles.previewOnDragOver : '')}
                    {...provided.droppableProps}
                  >
                    {items.map((item, index) => (
                      <Draggable key={`${item.file.name}-${item.now}`} draggableId={`${item.file.name}-${item.now}`} index={index}>
                        {(provided2, snapshot2) => (
                          <div
                            ref={provided2.innerRef}
                            {...provided2.draggableProps}
                            {...provided2.dragHandleProps}
                            style={provided2.draggableProps.style}
                            className={cn(styles.banner, snapshot2.isDragging ? styles.bannerOnDrag : '')}
                          >
                            <Image
                              className={styles.banner}
                              aspectRatio={(210/297)}
                              src={item.crop.blobUrl}
                            />
                            <IconButton className={styles.closeBtn} onClick={() => onCancel(index)}>
                              <CloseIcon />
                            </IconButton>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </Paper>
          <p className={styles.bannerDescription}>{t('jetso.firstLeafletCover')}</p>
        </>
      }
      <Paper className={styles.paper} elevation={2}>
        <DropzoneAreaBase
          dropzoneClass="mui-dragzone"
          dropzoneText={t('jetso.dragNDropFile')}
          acceptedFiles={['image/jpeg', 'image/png']}
          showAlerts={false}
          onAdd={onAddImage}
        />
      </Paper>
      <p className={styles.leafletRequirement}>{t('jetso.uploadLeafletRequirement')}</p>
      <HowItDesignSection />
      <Paper className={cn(styles.professional)} elevation={2}>
        <p>{t('jetso.professionalHelp')}</p>
        <FillButton
          className={cn(styles.btnDesign, 'mui-btn-link')}>
          <a
            href = "mailto:kingwu@creatisoft.io"
          >
            {t('common.contactUs')}
          </a>
        </FillButton>
      </Paper>
      {
        isCropOpen &&
        <CropperDialog
          src={selectedFile.data}
          onClose={handleCloseCropDialog}
          onCrop={_onCrop}
          isCropBoxFullSize
          cropOption={{
            format: 'image/jpeg',
            croppedImageWidth: 849,
            croppedImageHeight: 1200,
          }}
        /> 
      }
    </Page>
  )
}

export default UploadLeafletForm